/**
 * CommercialManagementConsole — Package 2A operational UI.
 *
 * Full CRUD + versioning + lifecycle management for Package 2 entities
 * (Contracts, Campaigns, Pricing Rules, Statement Rules, Assignments)
 * exclusively via /api/engage/v2/* canonical routes.
 *
 * Design language reuses existing shadcn primitives. No redesign.
 * No frontend-only data models — every field/enum mirrors the backend.
 * Statements-only; no payment execution.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Separator } from "../components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/engage/v2`;

// Canonical enums (mirror backend — do NOT invent new values)
const CONTRACT_TYPES = [
  { value: "agency_charity",     label: "Agency ↔ Charity" },
  { value: "agency_network",     label: "Agency ↔ Network" },
  { value: "agency_fundraiser",  label: "Agency ↔ Fundraiser" },
  { value: "network_charity",    label: "Network ↔ Charity" },
];
const CONTRACT_STATUSES = ["draft", "pending", "active", "suspended", "expired", "terminated"];
const CAMPAIGN_TYPES = ["door_to_door", "street", "private_site", "events", "digital", "other"];
const CAMPAIGN_STATUSES = ["draft", "active", "paused", "closed", "archived"];
const FUNDRAISING_METHODS = ["regular_giving", "one_off", "lottery", "referral", "mixed"];
const PRICING_MODELS = ["fixed", "tiered", "range"];
const STATEMENT_FREQUENCIES = ["weekly", "fortnightly", "monthly"];
const STATEMENT_GROUPINGS = ["organisation", "campaign", "fundraiser"];
const VAT_HANDLINGS = ["exempt", "inclusive", "exclusive"];
const APPROVAL_WORKFLOWS = ["none", "dual", "committee"];
const ASSIGNMENT_TARGETS = ["organisation", "fundraiser", "team", "venue"];
const ASSIGNMENT_STATUSES = ["active", "paused", "ended"];
const WRITE_ROLES = new Set([
  "platform_owner", "platform_administrator",
  "owner", "director", "admin", "manager", "regional_manager",
]);
const PLATFORM_ROLES = new Set(["platform_owner", "platform_administrator"]);

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

const auth = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("taptrack_token") || ""}` },
});

const readCurrentUser = () => {
  try {
    const u = sessionStorage.getItem("taptrack_user");
    return u ? JSON.parse(u) : null;
  } catch {
    return null;
  }
};

const humaniseError = (e, fallback = "Something went wrong.") => {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  return d?.message || d?.detail?.message || d?.detail?.code ||
         d?.detail || e?.message || fallback;
};

const isoDate = (v) => (v ? String(v).slice(0, 10) : "");

// ─────────────────────────────────────────────────────────────────────────
// Reusable UI primitives (empty state, badges, states)
// ─────────────────────────────────────────────────────────────────────────

function StatusBadge({ value }) {
  return <Badge variant="outline" data-testid={`status-badge-${value}`}>{value}</Badge>;
}

function EmptyState({ label, testid }) {
  return (
    <div className="py-10 text-center text-sm text-muted-foreground"
         data-testid={testid || `${label}-empty`}>
      No {label} yet.
    </div>
  );
}

function LoadingRow({ colSpan = 5 }) {
  return (
    <TableRow><TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground"
                          data-testid="loading-row">Loading…</TableCell></TableRow>
  );
}

function ErrorAlert({ error, testid = "error-alert" }) {
  if (!error) return null;
  return (
    <div className="rounded-md border border-red-300 bg-red-50 text-red-900 text-sm p-3"
         data-testid={testid}>{error}</div>
  );
}

function PermissionDenied() {
  return (
    <div className="rounded-md border p-6 text-center text-muted-foreground"
         data-testid="permission-denied">
      You do not have permission to view or manage this resource.
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Data-fetching hook
// ─────────────────────────────────────────────────────────────────────────

function useResource(path) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}${path}`, auth());
      const arr = Object.values(r.data).find((v) => Array.isArray(v)) || [];
      setRows(arr);
    } catch (e) {
      setError(humaniseError(e, "Failed to load records."));
    } finally { setLoading(false); }
  }, [path]);
  useEffect(() => { refresh(); }, [refresh]);
  return { rows, loading, error, refresh, setRows };
}

// ─────────────────────────────────────────────────────────────────────────
// Wizard: Contract Create
// ─────────────────────────────────────────────────────────────────────────

function ContractCreateWizard({ open, onOpenChange, onCreated, user, campaigns, pricingRules, statementRules }) {
  const orgFixed = !PLATFORM_ROLES.has(user?.role);
  const defaultOrg = user?.organisationId || "";
  const [form, setForm] = useState({
    contractType: "agency_charity",
    organisationIds: orgFixed ? [defaultOrg] : [],
    organisationIdsRaw: orgFixed ? defaultOrg : "",
    effectiveDate: new Date().toISOString().slice(0, 10),
    endDate: "",
    status: "draft",
    campaignIds: [], pricingRuleIds: [], statementRuleIds: [],
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const toggleRef = (list, id) => list.includes(id)
    ? list.filter((x) => x !== id) : [...list, id];

  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const orgIds = orgFixed
        ? [defaultOrg]
        : form.organisationIdsRaw.split(",").map((s) => s.trim()).filter(Boolean);
      if (!orgIds.length) throw new Error("At least one organisationId is required.");
      const body = {
        contractType: form.contractType,
        organisationIds: orgIds,
        effectiveDate: form.effectiveDate,
        endDate: form.endDate || null,
        status: form.status,
        campaignIds: form.campaignIds,
        pricingRuleIds: form.pricingRuleIds,
        statementRuleIds: form.statementRuleIds,
        notes: form.notes || null,
      };
      const r = await axios.post(`${API}/contracts`, body, auth());
      toast.success("Contract created.");
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      setErr(humaniseError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="contract-create-wizard">
        <DialogHeader>
          <DialogTitle>Create Contract</DialogTitle>
          <DialogDescription>
            Package 2 contracts are immutable. This creates version 1. Later
            edits create new immutable versions; history cannot be edited or deleted.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Contract type <span className="text-red-500">*</span></Label>
            <Select value={form.contractType}
                    onValueChange={(v) => setForm({ ...form, contractType: v })}>
              <SelectTrigger data-testid="contract-create-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRACT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="contract-create-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRACT_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Governed organisation IDs <span className="text-red-500">*</span></Label>
            <Input value={form.organisationIdsRaw}
                   onChange={(e) => setForm({ ...form, organisationIdsRaw: e.target.value })}
                   disabled={orgFixed}
                   placeholder={orgFixed ? "Fixed to your organisation" : "org-1, org-2"}
                   data-testid="contract-create-orgs" />
            {orgFixed && (
              <p className="text-xs text-muted-foreground mt-1">
                Organisation ID is fixed to your authenticated organisation and cannot be changed.
              </p>
            )}
          </div>
          <div>
            <Label>Effective date <span className="text-red-500">*</span></Label>
            <Input type="date" value={form.effectiveDate}
                   onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
                   data-testid="contract-create-effective-date" />
          </div>
          <div>
            <Label>End date (optional)</Label>
            <Input type="date" value={form.endDate}
                   onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                   data-testid="contract-create-end-date" />
          </div>
          <ReferencePicker label="Campaign references" items={campaigns}
            selected={form.campaignIds}
            onToggle={(id) => setForm({ ...form, campaignIds: toggleRef(form.campaignIds, id) })}
            testid="contract-create-campaigns" />
          <ReferencePicker label="Pricing rule references" items={pricingRules}
            selected={form.pricingRuleIds}
            onToggle={(id) => setForm({ ...form, pricingRuleIds: toggleRef(form.pricingRuleIds, id) })}
            testid="contract-create-pricing" />
          <ReferencePicker label="Statement rule references" items={statementRules}
            selected={form.statementRuleIds}
            onToggle={(id) => setForm({ ...form, statementRuleIds: toggleRef(form.statementRuleIds, id) })}
            testid="contract-create-statements" />
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} rows={2}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="contract-create-notes" />
          </div>
        </div>
        <ErrorAlert error={err} testid="contract-create-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="contract-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="contract-create-submit">
            {submitting ? "Creating…" : "Create Contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReferencePicker({ label, items, selected, onToggle, testid }) {
  return (
    <div className="col-span-2">
      <Label>{label}</Label>
      <div className="max-h-32 overflow-y-auto border rounded-md p-2 text-xs space-y-1"
           data-testid={testid}>
        {items.length === 0 ? (
          <div className="text-muted-foreground text-xs">No records available in your scope.</div>
        ) : items.map((it) => (
          <label key={it.id} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={selected.includes(it.id)}
                   onChange={() => onToggle(it.id)}
                   data-testid={`${testid}-item-${it.id}`} />
            <span className="font-mono">{it.id}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Wizard: Contract Version
// ─────────────────────────────────────────────────────────────────────────

function ContractVersionWizard({ open, onOpenChange, contract, currentVersion, onVersioned, campaigns, pricingRules, statementRules }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (currentVersion) {
      setForm({
        status: currentVersion.status,
        effectiveDate: isoDate(currentVersion.effectiveDate),
        endDate: isoDate(currentVersion.endDate) || "",
        campaignIds: currentVersion.campaignIds || [],
        pricingRuleIds: currentVersion.pricingRuleIds || [],
        statementRuleIds: currentVersion.statementRuleIds || [],
        notes: currentVersion.notes || "",
      });
    }
  }, [currentVersion, open]);

  const toggleRef = (list, id) => list.includes(id)
    ? list.filter((x) => x !== id) : [...list, id];

  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const body = { ...form, endDate: form.endDate || null };
      const r = await axios.post(`${API}/contracts/${contract.id}/versions`, body, auth());
      toast.success(`Version ${r.data.versionNumber} created — previous version preserved.`);
      onVersioned?.(r.data);
      onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };

  if (!contract || !currentVersion) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="contract-version-wizard">
        <DialogHeader>
          <DialogTitle>Create Contract Version</DialogTitle>
          <DialogDescription>
            <strong>The previous version remains unchanged.</strong> Creating a
            new version writes a new immutable record. The new version becomes
            current only after successful creation. Historical records cannot be
            edited or deleted.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Status</Label>
            <Select value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="contract-version-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRACT_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Effective date</Label>
            <Input type="date" value={form.effectiveDate}
                   onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
                   data-testid="contract-version-effective-date" />
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <div>
              <Label>End date (optional)</Label>
              <Input type="date" value={form.endDate}
                     onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                     data-testid="contract-version-end-date" />
            </div>
          </div>
          <ReferencePicker label="Campaign references" items={campaigns}
            selected={form.campaignIds || []}
            onToggle={(id) => setForm({ ...form, campaignIds: toggleRef(form.campaignIds || [], id) })}
            testid="contract-version-campaigns" />
          <ReferencePicker label="Pricing rule references" items={pricingRules}
            selected={form.pricingRuleIds || []}
            onToggle={(id) => setForm({ ...form, pricingRuleIds: toggleRef(form.pricingRuleIds || [], id) })}
            testid="contract-version-pricing" />
          <ReferencePicker label="Statement rule references" items={statementRules}
            selected={form.statementRuleIds || []}
            onToggle={(id) => setForm({ ...form, statementRuleIds: toggleRef(form.statementRuleIds || [], id) })}
            testid="contract-version-statements" />
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} rows={2}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="contract-version-notes" />
          </div>
        </div>
        <ErrorAlert error={err} testid="contract-version-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="contract-version-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="contract-version-submit">
            {submitting ? "Creating…" : "Create New Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Wizard: Campaign Create + Version
// ─────────────────────────────────────────────────────────────────────────

function CampaignForm({ form, setForm, testid, isVersion }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {!isVersion && (
        <div className="col-span-2">
          <Label>Charity organisation ID <span className="text-red-500">*</span></Label>
          <Input value={form.charityId || ""} disabled
                 data-testid={`${testid}-charity`} />
          <p className="text-xs text-muted-foreground mt-1">
            Fixed to your organisation. Cross-organisation writes are rejected.
          </p>
        </div>
      )}
      <div className="col-span-2">
        <Label>Campaign name <span className="text-red-500">*</span></Label>
        <Input value={form.name || ""}
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               data-testid={`${testid}-name`} />
      </div>
      <div>
        <Label>Campaign type</Label>
        <Select value={form.campaignType} onValueChange={(v) => setForm({ ...form, campaignType: v })}>
          <SelectTrigger data-testid={`${testid}-type`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {CAMPAIGN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Status</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger data-testid={`${testid}-status`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {CAMPAIGN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Start date <span className="text-red-500">*</span></Label>
        <Input type="date" value={form.startDate || ""}
               onChange={(e) => setForm({ ...form, startDate: e.target.value })}
               data-testid={`${testid}-start`} />
      </div>
      <div>
        <Label>End date (optional)</Label>
        <Input type="date" value={form.endDate || ""}
               onChange={(e) => setForm({ ...form, endDate: e.target.value })}
               data-testid={`${testid}-end`} />
      </div>
      <div>
        <Label>Minimum age</Label>
        <Input type="number" value={form.minimumAge ?? 18} min={0}
               onChange={(e) => setForm({ ...form, minimumAge: parseInt(e.target.value || "18", 10) })}
               data-testid={`${testid}-min-age`} />
      </div>
      <div>
        <Label>Fundraising method</Label>
        <Select value={form.fundraisingMethod}
                onValueChange={(v) => setForm({ ...form, fundraisingMethod: v })}>
          <SelectTrigger data-testid={`${testid}-method`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {FUNDRAISING_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Label>Supported products (comma-separated)</Label>
        <Input value={(form.supportedProducts || []).join(", ")}
               onChange={(e) => setForm({ ...form, supportedProducts:
                 e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
               data-testid={`${testid}-products`} />
      </div>
      <div className="col-span-2 flex items-center gap-6">
        <label className="flex items-center gap-2">
          <Switch checked={!!form.referralEnabled}
                  onCheckedChange={(v) => setForm({ ...form, referralEnabled: v })}
                  data-testid={`${testid}-referral`} />
          <span>Referral enabled</span>
        </label>
        <label className="flex items-center gap-2">
          <Switch checked={form.statementsEnabled !== false}
                  onCheckedChange={(v) => setForm({ ...form, statementsEnabled: v })}
                  data-testid={`${testid}-statements`} />
          <span>Statements enabled (informational only)</span>
        </label>
      </div>
    </div>
  );
}

function CampaignCreateWizard({ open, onOpenChange, onCreated, user }) {
  const [form, setForm] = useState({
    charityId: user?.organisationId || "",
    name: "", campaignType: "door_to_door", status: "draft",
    startDate: new Date().toISOString().slice(0, 10), endDate: "",
    minimumAge: 18, fundraisingMethod: "regular_giving",
    supportedProducts: [], referralEnabled: false, statementsEnabled: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      if (!form.name.trim()) throw new Error("Campaign name is required.");
      const body = { ...form, endDate: form.endDate || null };
      const r = await axios.post(`${API}/campaigns`, body, auth());
      toast.success("Campaign created.");
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="campaign-create-wizard">
        <DialogHeader>
          <DialogTitle>Create Campaign</DialogTitle>
          <DialogDescription>Version 1 of a governed campaign. Later edits create new immutable versions.</DialogDescription>
        </DialogHeader>
        <CampaignForm form={form} setForm={setForm} testid="campaign-create" />
        <ErrorAlert error={err} testid="campaign-create-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="campaign-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="campaign-create-submit">
            {submitting ? "Creating…" : "Create Campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignVersionWizard({ open, onOpenChange, campaign, currentVersion, onVersioned }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (currentVersion) setForm({ ...currentVersion,
      startDate: isoDate(currentVersion.startDate),
      endDate: isoDate(currentVersion.endDate) }); }, [currentVersion, open]);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const body = { ...form, endDate: form.endDate || null };
      delete body.id; delete body.campaignId; delete body.versionNumber;
      delete body.createdBy; delete body.createdAt; delete body.charityId;
      const r = await axios.post(`${API}/campaigns/${campaign.id}/versions`, body, auth());
      toast.success(`Version ${r.data.versionNumber} created — previous version preserved.`);
      onVersioned?.(r.data); onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };
  if (!campaign || !currentVersion) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="campaign-version-wizard">
        <DialogHeader>
          <DialogTitle>Create Campaign Version</DialogTitle>
          <DialogDescription>
            The previous version remains unchanged. The new version becomes
            current only after successful creation.
          </DialogDescription>
        </DialogHeader>
        <CampaignForm form={form} setForm={setForm} testid="campaign-version" isVersion />
        <ErrorAlert error={err} testid="campaign-version-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="campaign-version-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="campaign-version-submit">
            {submitting ? "Creating…" : "Create New Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Wizard: Pricing Rule Create + Version + Preview
// ─────────────────────────────────────────────────────────────────────────

function TierEditor({ tiers, onChange, testid }) {
  const setTier = (i, patch) => onChange(tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const addTier = () => onChange([...tiers, { fromUnits: 0, amountPence: 0 }]);
  const removeTier = (i) => onChange(tiers.filter((_, idx) => idx !== i));
  return (
    <div className="col-span-2 space-y-2" data-testid={testid}>
      <Label>Tiers</Label>
      {tiers.map((t, i) => (
        <div key={i} className="grid grid-cols-4 gap-2 items-end">
          <div><Label className="text-xs">From units</Label>
            <Input type="number" value={t.fromUnits}
                   onChange={(e) => setTier(i, { fromUnits: parseInt(e.target.value || "0", 10) })}
                   data-testid={`${testid}-from-${i}`} /></div>
          <div><Label className="text-xs">To units (optional)</Label>
            <Input type="number" value={t.toUnits ?? ""}
                   onChange={(e) => setTier(i, { toUnits: e.target.value === "" ? undefined : parseInt(e.target.value, 10) })}
                   data-testid={`${testid}-to-${i}`} /></div>
          <div><Label className="text-xs">Amount (pence)</Label>
            <Input type="number" value={t.amountPence}
                   onChange={(e) => setTier(i, { amountPence: parseInt(e.target.value || "0", 10) })}
                   data-testid={`${testid}-amount-${i}`} /></div>
          <Button variant="outline" size="sm" onClick={() => removeTier(i)}
                  data-testid={`${testid}-remove-${i}`}>Remove</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addTier}
              data-testid={`${testid}-add`}>Add tier</Button>
    </div>
  );
}

function PricingPreview({ form }) {
  // A pure client-side preview — clearly labelled — does NOT create a statement or payment.
  const [units, setUnits] = useState(10);
  const preview = useMemo(() => {
    if (form.pricingModel === "fixed") {
      const p = form.amountPence || 0;
      return { total: p * units, note: `£${(p / 100).toFixed(2)} × ${units} = £${(p * units / 100).toFixed(2)}` };
    }
    if (form.pricingModel === "range") {
      const lo = form.minPence || 0, hi = form.maxPence || 0;
      return { total: null, note: `Any value between £${(lo / 100).toFixed(2)} and £${(hi / 100).toFixed(2)} (per unit)` };
    }
    if (form.pricingModel === "tiered") {
      const tiers = (form.tiers || []).slice().sort((a, b) => (a.fromUnits || 0) - (b.fromUnits || 0));
      let total = 0, remaining = units, applied = [];
      for (const t of tiers) {
        const from = t.fromUnits || 0;
        const to = (t.toUnits !== undefined && t.toUnits !== null) ? t.toUnits : Infinity;
        const width = Math.max(0, Math.min(units, to + 1) - from);
        if (width > 0 && from <= units) {
          const applicable = Math.min(width, remaining);
          total += (t.amountPence || 0) * applicable;
          applied.push(`${applicable} × £${((t.amountPence || 0) / 100).toFixed(2)}`);
          remaining -= applicable;
        }
      }
      return { total, note: applied.join(" + ") || "No tiers cover this quantity." };
    }
    return { total: null, note: "" };
  }, [form, units]);

  return (
    <div className="col-span-2 rounded-md border p-3 bg-muted/40" data-testid="pricing-preview">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Configuration preview</div>
          <div className="text-xs">Illustrative only. This is not a statement, invoice or payment.</div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Sample units</Label>
          <Input type="number" value={units} min={0} className="w-24"
                 onChange={(e) => setUnits(parseInt(e.target.value || "0", 10))}
                 data-testid="pricing-preview-units" />
        </div>
      </div>
      <div className="mt-2 text-sm">
        {preview.total !== null && (
          <div data-testid="pricing-preview-total">
            <strong>Estimated total: £{(preview.total / 100).toFixed(2)}</strong> (for {units} units)
          </div>
        )}
        <div className="text-xs text-muted-foreground mt-1"
             data-testid="pricing-preview-explanation">{preview.note}</div>
      </div>
    </div>
  );
}

function PricingRuleForm({ form, setForm, testid, onLocalErrorChange }) {
  const localErr = useMemo(() => {
    if (form.pricingModel === "fixed" &&
        (form.amountPence === undefined || form.amountPence === null || form.amountPence < 0))
      return "Fixed pricing requires a non-negative amountPence.";
    if (form.pricingModel === "range") {
      if ((form.minPence ?? 0) < 0 || (form.maxPence ?? 0) < 0)
        return "Range values cannot be negative.";
      if ((form.minPence ?? 0) > (form.maxPence ?? 0))
        return "Range min must be ≤ max.";
    }
    if (form.pricingModel === "tiered") {
      const ts = (form.tiers || []).slice().sort((a, b) => (a.fromUnits || 0) - (b.fromUnits || 0));
      for (let i = 0; i < ts.length; i++) {
        if ((ts[i].amountPence ?? 0) < 0) return "Tier amounts cannot be negative.";
        if (ts[i].toUnits !== undefined && ts[i].toUnits !== null &&
            ts[i].toUnits < ts[i].fromUnits) return "Tier toUnits must be ≥ fromUnits.";
        if (i > 0) {
          const prev = ts[i - 1];
          const prevTo = (prev.toUnits ?? Infinity);
          if (prevTo >= ts[i].fromUnits) return "Tiers must not overlap.";
        }
      }
    }
    return null;
  }, [form]);
  useEffect(() => { onLocalErrorChange?.(localErr); }, [localErr, onLocalErrorChange]);
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Pricing model</Label>
          <Select value={form.pricingModel}
                  onValueChange={(v) => setForm({ ...form, pricingModel: v })}>
            <SelectTrigger data-testid={`${testid}-model`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRICING_MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Product (optional)</Label>
          <Input value={form.product || ""}
                 onChange={(e) => setForm({ ...form, product: e.target.value })}
                 data-testid={`${testid}-product`}
                 placeholder="regular_giving | one_off | lottery | referral" />
        </div>
        <div>
          <Label>Campaign id (optional)</Label>
          <Input value={form.campaignId || ""}
                 onChange={(e) => setForm({ ...form, campaignId: e.target.value })}
                 data-testid={`${testid}-campaign`} />
        </div>
        <div>
          <Label>Fundraiser id (optional)</Label>
          <Input value={form.fundraiserId || ""}
                 onChange={(e) => setForm({ ...form, fundraiserId: e.target.value })}
                 data-testid={`${testid}-fundraiser`} />
        </div>
        {form.pricingModel === "fixed" && (
          <div className="col-span-2">
            <Label>Amount (pence)</Label>
            <Input type="number" value={form.amountPence ?? 0} min={0}
                   onChange={(e) => setForm({ ...form, amountPence: parseInt(e.target.value || "0", 10) })}
                   data-testid={`${testid}-amount`} />
          </div>
        )}
        {form.pricingModel === "range" && (
          <>
            <div>
              <Label>Min (pence)</Label>
              <Input type="number" value={form.minPence ?? 0} min={0}
                     onChange={(e) => setForm({ ...form, minPence: parseInt(e.target.value || "0", 10) })}
                     data-testid={`${testid}-min`} />
            </div>
            <div>
              <Label>Max (pence)</Label>
              <Input type="number" value={form.maxPence ?? 0} min={0}
                     onChange={(e) => setForm({ ...form, maxPence: parseInt(e.target.value || "0", 10) })}
                     data-testid={`${testid}-max`} />
            </div>
          </>
        )}
        {form.pricingModel === "tiered" && (
          <TierEditor tiers={form.tiers || []}
                       onChange={(t) => setForm({ ...form, tiers: t })}
                       testid={`${testid}-tiers`} />
        )}
        <div>
          <Label>Effective from</Label>
          <Input type="date" value={isoDate(form.effectiveFrom)}
                 onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                 data-testid={`${testid}-effective-from`} />
        </div>
        <div>
          <Label>Effective to (optional)</Label>
          <Input type="date" value={isoDate(form.effectiveTo)}
                 onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })}
                 data-testid={`${testid}-effective-to`} />
        </div>
      </div>
      {localErr && (
        <div className="text-red-700 text-xs mt-2" data-testid={`${testid}-local-error`}>{localErr}</div>
      )}
      <PricingPreview form={form} />
    </>
  );
}

function PricingCreateWizard({ open, onOpenChange, onCreated, user }) {
  const orgFixed = !PLATFORM_ROLES.has(user?.role);
  const [form, setForm] = useState({
    organisationId: user?.organisationId || "",
    pricingModel: "fixed", amountPence: 1200, tiers: [],
    minPence: 0, maxPence: 0,
    effectiveFrom: new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [localErr, setLocalErr] = useState(null);
  const submit = async () => {
    if (localErr) { setErr(localErr); return; }
    setErr(null); setSubmitting(true);
    try {
      const body = { ...form, effectiveTo: form.effectiveTo || null };
      if (orgFixed) body.organisationId = user.organisationId;
      const r = await axios.post(`${API}/pricing-rules`, body, auth());
      toast.success("Pricing rule created.");
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="pricing-create-wizard">
        <DialogHeader>
          <DialogTitle>Create Pricing Rule</DialogTitle>
          <DialogDescription>Rule definitions only — never executes payments.</DialogDescription>
        </DialogHeader>
        {orgFixed && (
          <div className="text-xs text-muted-foreground" data-testid="pricing-create-org-fixed">
            Organisation: <span className="font-mono">{user?.organisationId}</span> (fixed to your organisation)
          </div>
        )}
        <PricingRuleForm form={form} setForm={setForm} testid="pricing-create"
                          onLocalErrorChange={setLocalErr} />
        <ErrorAlert error={err} testid="pricing-create-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="pricing-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting || !!localErr}
                  data-testid="pricing-create-submit">
            {submitting ? "Creating…" : "Create Pricing Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PricingVersionWizard({ open, onOpenChange, rule, currentVersion, onVersioned }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (currentVersion) setForm({ ...currentVersion,
      effectiveFrom: isoDate(currentVersion.effectiveFrom),
      effectiveTo: isoDate(currentVersion.effectiveTo) }); }, [currentVersion, open]);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const body = { ...form, effectiveTo: form.effectiveTo || null };
      delete body.id; delete body.pricingRuleId; delete body.versionNumber;
      delete body.createdBy; delete body.createdAt; delete body.organisationId;
      const r = await axios.post(`${API}/pricing-rules/${rule.id}/versions`, body, auth());
      toast.success(`Version ${r.data.versionNumber} created — previous version preserved.`);
      onVersioned?.(r.data); onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };
  if (!rule || !currentVersion) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="pricing-version-wizard">
        <DialogHeader>
          <DialogTitle>Create Pricing Rule Version</DialogTitle>
          <DialogDescription>Previous version remains unchanged.</DialogDescription>
        </DialogHeader>
        <PricingRuleForm form={form} setForm={setForm} testid="pricing-version" />
        <ErrorAlert error={err} testid="pricing-version-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="pricing-version-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="pricing-version-submit">
            {submitting ? "Creating…" : "Create New Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Wizard: Statement Rule Create + Version
// ─────────────────────────────────────────────────────────────────────────

function StatementRuleForm({ form, setForm, testid }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Frequency</Label>
        <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
          <SelectTrigger data-testid={`${testid}-frequency`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATEMENT_FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Grouping</Label>
        <Select value={form.grouping} onValueChange={(v) => setForm({ ...form, grouping: v })}>
          <SelectTrigger data-testid={`${testid}-grouping`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATEMENT_GROUPINGS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>VAT handling</Label>
        <Select value={form.vatHandling} onValueChange={(v) => setForm({ ...form, vatHandling: v })}>
          <SelectTrigger data-testid={`${testid}-vat`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {VAT_HANDLINGS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Approval workflow</Label>
        <Select value={form.approvalWorkflow}
                onValueChange={(v) => setForm({ ...form, approvalWorkflow: v })}>
          <SelectTrigger data-testid={`${testid}-approval`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {APPROVAL_WORKFLOWS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Label>Reference prefix</Label>
        <Input value={form.referencePrefix || ""}
               onChange={(e) => setForm({ ...form, referencePrefix: e.target.value })}
               data-testid={`${testid}-prefix`} />
      </div>
      <div>
        <Label>Effective from</Label>
        <Input type="date" value={isoDate(form.effectiveFrom)}
               onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
               data-testid={`${testid}-effective-from`} />
      </div>
      <div>
        <Label>Effective to (optional)</Label>
        <Input type="date" value={isoDate(form.effectiveTo)}
               onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })}
               data-testid={`${testid}-effective-to`} />
      </div>
    </div>
  );
}

function StatementCreateWizard({ open, onOpenChange, onCreated, user }) {
  const [form, setForm] = useState({
    organisationId: user?.organisationId || "",
    frequency: "weekly", grouping: "organisation", vatHandling: "exempt",
    referencePrefix: "STMT", approvalWorkflow: "none",
    effectiveFrom: new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const body = { ...form, effectiveTo: form.effectiveTo || null };
      const r = await axios.post(`${API}/statement-rules`, body, auth());
      toast.success("Statement rule created.");
      onCreated?.(r.data); onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="statement-create-wizard">
        <DialogHeader>
          <DialogTitle>Create Statement Rule</DialogTitle>
          <DialogDescription>
            <strong>Statements are informational only.</strong> No payment is executed.
            Financial execution is disabled.
          </DialogDescription>
        </DialogHeader>
        <StatementRuleForm form={form} setForm={setForm} testid="statement-create" />
        <ErrorAlert error={err} testid="statement-create-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="statement-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="statement-create-submit">
            {submitting ? "Creating…" : "Create Statement Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementVersionWizard({ open, onOpenChange, rule, currentVersion, onVersioned }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (currentVersion) setForm({ ...currentVersion,
      effectiveFrom: isoDate(currentVersion.effectiveFrom),
      effectiveTo: isoDate(currentVersion.effectiveTo) }); }, [currentVersion, open]);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      const body = { ...form, effectiveTo: form.effectiveTo || null };
      delete body.id; delete body.statementRuleId; delete body.versionNumber;
      delete body.createdBy; delete body.createdAt; delete body.organisationId;
      const r = await axios.post(`${API}/statement-rules/${rule.id}/versions`, body, auth());
      toast.success(`Version ${r.data.versionNumber} created — previous version preserved.`);
      onVersioned?.(r.data); onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };
  if (!rule || !currentVersion) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="statement-version-wizard">
        <DialogHeader>
          <DialogTitle>Create Statement Rule Version</DialogTitle>
          <DialogDescription>Previous version remains unchanged. Statements remain informational only.</DialogDescription>
        </DialogHeader>
        <StatementRuleForm form={form} setForm={setForm} testid="statement-version" />
        <ErrorAlert error={err} testid="statement-version-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="statement-version-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="statement-version-submit">
            {submitting ? "Creating…" : "Create New Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Wizard: Assignment Create + Lifecycle
// ─────────────────────────────────────────────────────────────────────────

function AssignmentCreateWizard({ open, onOpenChange, onCreated, user, campaigns }) {
  const [form, setForm] = useState({
    campaignId: "", targetType: "fundraiser", targetId: "",
    organisationId: user?.organisationId || "",
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: "", status: "active",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setErr(null); setSubmitting(true);
    try {
      if (!form.campaignId || !form.targetId) throw new Error("Campaign and target id are required.");
      const body = { ...form, effectiveTo: form.effectiveTo || null };
      const r = await axios.post(`${API}/assignments`, body, auth());
      toast.success("Assignment created.");
      onCreated?.(r.data); onOpenChange(false);
    } catch (e) { setErr(humaniseError(e)); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="assignment-create-wizard">
        <DialogHeader>
          <DialogTitle>Create Assignment</DialogTitle>
          <DialogDescription>
            Governs a campaign → target (fundraiser, team, venue or organisation) relationship.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Campaign</Label>
            <Select value={form.campaignId}
                    onValueChange={(v) => setForm({ ...form, campaignId: v })}>
              <SelectTrigger data-testid="assignment-create-campaign">
                <SelectValue placeholder="Choose a governed campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.length === 0 && (
                  <SelectItem value="__none__" disabled>No campaigns in your scope</SelectItem>
                )}
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Target type</Label>
            <Select value={form.targetType}
                    onValueChange={(v) => setForm({ ...form, targetType: v })}>
              <SelectTrigger data-testid="assignment-create-target-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSIGNMENT_TARGETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Target id</Label>
            <Input value={form.targetId}
                   onChange={(e) => setForm({ ...form, targetId: e.target.value })}
                   data-testid="assignment-create-target-id" />
          </div>
          <div>
            <Label>Effective from</Label>
            <Input type="date" value={form.effectiveFrom}
                   onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                   data-testid="assignment-create-effective-from" />
          </div>
          <div>
            <Label>Effective to (optional)</Label>
            <Input type="date" value={form.effectiveTo}
                   onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })}
                   data-testid="assignment-create-effective-to" />
          </div>
        </div>
        <ErrorAlert error={err} testid="assignment-create-error" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="assignment-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  data-testid="assignment-create-submit">
            {submitting ? "Creating…" : "Create Assignment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentLifecycleButton({ assignment, action, label, onChanged }) {
  const [busy, setBusy] = useState(false);
  const perform = async () => {
    setBusy(true);
    try {
      // Map lifecycle labels to backend statuses (only backend-supported values)
      const map = { activate: "active", suspend: "paused", expire: "ended", terminate: "ended" };
      const status = map[action];
      await axios.patch(`${API}/assignments/${assignment.id}`, { status }, auth());
      const past = { activate: "activated", suspend: "suspended", expire: "expired", terminate: "terminated" };
      toast.success(`Assignment ${past[action]}.`);
      onChanged?.();
    } catch (e) { toast.error(humaniseError(e)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy}
                data-testid={`assignment-action-${action}-${assignment.id}`}>{label}</Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`assignment-action-confirm-${action}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm assignment {action}</AlertDialogTitle>
          <AlertDialogDescription>
            This will transition assignment <span className="font-mono">{assignment.id}</span> and
            write an entry to its append-only history. Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`assignment-action-cancel-${action}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={perform}
            data-testid={`assignment-action-confirm-btn-${action}`}>Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Version History + Comparison
// ─────────────────────────────────────────────────────────────────────────

const VERSION_ENTITY_MAP = {
  contract:      { list: "/contracts",       versions: "/contracts",       label: "Contracts" },
  campaign:      { list: "/campaigns",       versions: "/campaigns",       label: "Campaigns" },
  pricingRule:   { list: "/pricing-rules",   versions: "/pricing-rules",   label: "Pricing Rules" },
  statementRule: { list: "/statement-rules", versions: "/statement-rules", label: "Statement Rules" },
};

function VersionHistoryPanel() {
  const [entity, setEntity] = useState("contract");
  const cfg = VERSION_ENTITY_MAP[entity];
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [currentId, setCurrentId] = useState("");
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");

  useEffect(() => {
    setItemsLoading(true);
    setSelectedId(""); setVersions([]);
    axios.get(`${API}${cfg.list}`, auth())
      .then((r) => setItems(Object.values(r.data).find((v) => Array.isArray(v)) || []))
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  }, [entity, cfg.list]);

  useEffect(() => {
    if (!selectedId) return;
    setVersionsLoading(true);
    axios.get(`${API}${cfg.versions}/${selectedId}/versions`, auth())
      .then((r) => { setVersions(r.data.versions || []); setCurrentId(r.data.currentVersionId || ""); })
      .catch(() => setVersions([]))
      .finally(() => setVersionsLoading(false));
  }, [selectedId, cfg.versions]);

  const va = versions.find((v) => v.id === compareA);
  const vb = versions.find((v) => v.id === compareB);
  const diffRows = useMemo(() => {
    if (!va || !vb) return [];
    const keys = Array.from(new Set([...Object.keys(va), ...Object.keys(vb)])).sort();
    return keys.filter((k) => !["id", "createdAt", "createdBy"].includes(k))
      .map((k) => ({ field: k, a: JSON.stringify(va[k]), b: JSON.stringify(vb[k]),
                      changed: JSON.stringify(va[k]) !== JSON.stringify(vb[k]) }));
  }, [va, vb]);

  return (
    <Card data-testid="version-history-panel">
      <CardHeader><CardTitle>Version History</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="w-56" data-testid="version-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(VERSION_ENTITY_MAP).map(([k, v]) =>
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Record</Label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="w-96" data-testid="version-record"><SelectValue placeholder="Choose a record" /></SelectTrigger>
              <SelectContent>
                {itemsLoading && <SelectItem value="__l__" disabled>Loading…</SelectItem>}
                {!itemsLoading && items.length === 0 && (
                  <SelectItem value="__n__" disabled>No records in scope</SelectItem>
                )}
                {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!selectedId ? <EmptyState label="version selection" testid="version-empty" /> : (
          <>
            <Table data-testid="version-list-table">
              <TableHeader><TableRow>
                <TableHead>Version</TableHead><TableHead>ID</TableHead>
                <TableHead>Created at</TableHead><TableHead>Created by</TableHead>
                <TableHead>Effective</TableHead><TableHead>Current?</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {versionsLoading && <LoadingRow colSpan={6} />}
                {!versionsLoading && versions.map((v) => (
                  <TableRow key={v.id} data-testid={`version-row-${v.id}`}>
                    <TableCell>{v.versionNumber}</TableCell>
                    <TableCell className="font-mono text-xs">{v.id}</TableCell>
                    <TableCell className="text-xs">{v.createdAt}</TableCell>
                    <TableCell className="font-mono text-xs">{v.createdBy}</TableCell>
                    <TableCell className="text-xs">
                      {isoDate(v.effectiveDate || v.effectiveFrom || v.startDate || "")}
                    </TableCell>
                    <TableCell>
                      {v.id === currentId ? <Badge data-testid={`version-current-${v.id}`}>current</Badge> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label>Compare A</Label>
                <Select value={compareA} onValueChange={setCompareA}>
                  <SelectTrigger className="w-72" data-testid="version-compare-a"><SelectValue placeholder="Version A" /></SelectTrigger>
                  <SelectContent>
                    {versions.map((v) =>
                      <SelectItem key={v.id} value={v.id}>#{v.versionNumber} · {v.id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Compare B</Label>
                <Select value={compareB} onValueChange={setCompareB}>
                  <SelectTrigger className="w-72" data-testid="version-compare-b"><SelectValue placeholder="Version B" /></SelectTrigger>
                  <SelectContent>
                    {versions.map((v) =>
                      <SelectItem key={v.id} value={v.id}>#{v.versionNumber} · {v.id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {va && vb && (
              <div data-testid="version-diff">
                <Separator className="my-3" />
                <div className="text-sm font-semibold">Field-level differences (read-only)</div>
                <div className="text-xs text-muted-foreground mb-2">
                  Historical versions are immutable. This view cannot edit them.
                </div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Field</TableHead>
                    <TableHead>#{va.versionNumber}</TableHead>
                    <TableHead>#{vb.versionNumber}</TableHead>
                    <TableHead>Changed?</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {diffRows.map((r) => (
                      <TableRow key={r.field}
                        data-testid={`version-diff-row-${r.field}`}
                        className={r.changed ? "bg-amber-50" : ""}>
                        <TableCell className="font-mono text-xs">{r.field}</TableCell>
                        <TableCell className="font-mono text-xs">{r.a}</TableCell>
                        <TableCell className="font-mono text-xs">{r.b}</TableCell>
                        <TableCell>{r.changed ? <Badge>changed</Badge> : ""}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Route Inventory (kept from Package 2)
// ─────────────────────────────────────────────────────────────────────────

function RouteInventoryPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/route-inventory`, auth())
      .then((r) => setData(r.data))
      .catch((e) => setError(humaniseError(e)));
  }, []);
  if (error) return <ErrorAlert error={error} testid="routes-error" />;
  if (!data) return <div data-testid="routes-loading">Loading…</div>;
  return (
    <Card data-testid="engage-v2-route-inventory">
      <CardHeader><CardTitle>Route inventory (proof of no duplication)</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <StatBox label="Engage v2 routes" value={data.engageV2Count} testid="routes-v2-count" />
          <StatBox label="Legacy routes (untouched)" value={data.legacyCount} testid="routes-legacy-count" />
          <StatBox label="Duplicates within v2" value={data.duplicatesWithinV2.length} testid="routes-dup-count" />
        </div>
        <div className="text-xs text-muted-foreground">
          Statements-only: <strong>{String(data.statementsOnly)}</strong> ·
          Financial execution enabled: <strong>{String(data.financialExecutionEnabled)}</strong>
        </div>
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="rounded-lg border bg-muted/30 px-4 py-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab: Contracts
// ─────────────────────────────────────────────────────────────────────────

function ContractsTab({ user, campaigns, pricingRules, statementRules, canWrite }) {
  const { rows, loading, error, refresh } = useResource("/contracts");
  const [typeFilter, setTypeFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [orgFilter, setOrgFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedCurrentVersion, setSelectedCurrentVersion] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);

  const enriched = useMemo(() => rows.map((c) => ({
    ...c,
    _search: [c.id, ...(c.organisationIds || []), c.contractType].join(" ").toLowerCase(),
  })), [rows]);

  const filtered = enriched.filter((c) =>
    (typeFilter === "__all__" || c.contractType === typeFilter) &&
    (orgFilter === "" || (c.organisationIds || []).includes(orgFilter)) &&
    (search === "" || c._search.includes(search.toLowerCase()))
    // status filter checks current version status via fetched detail — simplified inline
  );

  const openDetails = async (c) => {
    setSelected(c);
    setSelectedCurrentVersion(null);
    try {
      const r = await axios.get(`${API}/contracts/${c.id}`, auth());
      setSelectedCurrentVersion(r.data.currentVersion);
    } catch (e) { toast.error(humaniseError(e)); }
  };

  const statusFilteredIds = statusFilter === "__all__" ? null :
    // We do a lightweight per-row check via already-loaded currentVersion when open.
    null; // status filtering below is applied only via version fetches per open row.

  return (
    <Card data-testid="engage-v2-contracts-card">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Contracts</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh}
                  data-testid="contracts-refresh">Refresh</Button>
          {canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    data-testid="contracts-create-btn">New Contract</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Contract type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="contracts-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All types</SelectItem>
                {CONTRACT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="contracts-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {CONTRACT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Organisation id</Label>
            <Input value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}
                   placeholder="Filter by governed org id"
                   data-testid="contracts-filter-org" />
          </div>
          <div>
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Contract id or counterparty"
                   data-testid="contracts-filter-search" />
          </div>
        </div>
        <ErrorAlert error={error} />
        <Table data-testid="engage-v2-contracts-table">
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Type</TableHead>
            <TableHead>Organisations</TableHead><TableHead>Current version</TableHead>
            <TableHead>Updated</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {loading && <LoadingRow colSpan={6} />}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={6}><EmptyState label="contracts" /></TableCell></TableRow>}
            {!loading && filtered.map((c) => (
              <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                <TableCell className="font-mono text-xs">{c.id}</TableCell>
                <TableCell><Badge variant="outline">{c.contractType}</Badge></TableCell>
                <TableCell>{(c.organisationIds || []).length}</TableCell>
                <TableCell className="font-mono text-xs">{c.currentVersionId}</TableCell>
                <TableCell className="text-xs">{c.updatedAt}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openDetails(c)}
                          data-testid={`contract-open-${c.id}`}>Details</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {selected && (
          <Card className="mt-4" data-testid="contract-details">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Contract <span className="font-mono text-sm">{selected.id}</span>
              </CardTitle>
              <div className="flex gap-2">
                {canWrite && selectedCurrentVersion && (
                  <Button size="sm" onClick={() => setVersionOpen(true)}
                          data-testid="contract-new-version-btn">New version</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}
                        data-testid="contract-close">Close</Button>
              </div>
            </CardHeader>
            <CardContent>
              {!selectedCurrentVersion ? <LoadingRow /> : (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><strong>Type:</strong> {selectedCurrentVersion.contractType}</div>
                  <div><strong>Status:</strong> <StatusBadge value={selectedCurrentVersion.status} /></div>
                  <div><strong>Effective:</strong> {isoDate(selectedCurrentVersion.effectiveDate)}</div>
                  <div><strong>End:</strong> {isoDate(selectedCurrentVersion.endDate) || "—"}</div>
                  <div><strong>Version #:</strong> {selectedCurrentVersion.versionNumber}</div>
                  <div><strong>Governed orgs:</strong> {(selectedCurrentVersion.organisationIds || []).join(", ")}</div>
                  <div className="col-span-2">
                    <strong>Campaign refs:</strong> {(selectedCurrentVersion.campaignIds || []).join(", ") || "—"}
                  </div>
                  <div className="col-span-2">
                    <strong>Pricing rule refs:</strong> {(selectedCurrentVersion.pricingRuleIds || []).join(", ") || "—"}
                  </div>
                  <div className="col-span-2">
                    <strong>Statement rule refs:</strong> {(selectedCurrentVersion.statementRuleIds || []).join(", ") || "—"}
                  </div>
                  {selectedCurrentVersion.notes && (
                    <div className="col-span-2"><strong>Notes:</strong> {selectedCurrentVersion.notes}</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </CardContent>

      <ContractCreateWizard open={createOpen} onOpenChange={setCreateOpen}
        onCreated={() => refresh()} user={user}
        campaigns={campaigns} pricingRules={pricingRules} statementRules={statementRules} />

      <ContractVersionWizard open={versionOpen} onOpenChange={setVersionOpen}
        contract={selected} currentVersion={selectedCurrentVersion}
        onVersioned={() => { refresh(); if (selected) openDetails(selected); }}
        campaigns={campaigns} pricingRules={pricingRules} statementRules={statementRules} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab: Campaigns
// ─────────────────────────────────────────────────────────────────────────

function CampaignsTab({ user, canWrite }) {
  const { rows, loading, refresh } = useResource("/campaigns");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [charityFilter, setCharityFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedCurrentVersion, setSelectedCurrentVersion] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);

  const filtered = rows.filter((c) => charityFilter === "" || c.charityId === charityFilter);

  const openDetails = async (c) => {
    setSelected(c); setSelectedCurrentVersion(null);
    try {
      const r = await axios.get(`${API}/campaigns/${c.id}`, auth());
      setSelectedCurrentVersion(r.data.currentVersion);
    } catch (e) { toast.error(humaniseError(e)); }
  };

  return (
    <Card data-testid="engage-v2-campaigns-card">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Campaigns</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh}
                  data-testid="campaigns-refresh">Refresh</Button>
          {canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    data-testid="campaigns-create-btn">New Campaign</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Charity id</Label>
            <Input value={charityFilter} onChange={(e) => setCharityFilter(e.target.value)}
                   data-testid="campaigns-filter-charity" />
          </div>
          <div>
            <Label className="text-xs">Status (via detail)</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="campaigns-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {CAMPAIGN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Table data-testid="engage-v2-campaigns-table">
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Charity</TableHead>
            <TableHead>Current version</TableHead><TableHead>Updated</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {loading && <LoadingRow colSpan={5} />}
            {!loading && filtered.length === 0 &&
              <TableRow><TableCell colSpan={5}><EmptyState label="campaigns" /></TableCell></TableRow>}
            {!loading && filtered.map((c) => (
              <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                <TableCell className="font-mono text-xs">{c.id}</TableCell>
                <TableCell className="font-mono text-xs">{c.charityId}</TableCell>
                <TableCell className="font-mono text-xs">{c.currentVersionId}</TableCell>
                <TableCell className="text-xs">{c.updatedAt}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openDetails(c)}
                          data-testid={`campaign-open-${c.id}`}>Details</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {selected && selectedCurrentVersion && (
          <Card className="mt-4" data-testid="campaign-details">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">
                {selectedCurrentVersion.name} <span className="font-mono text-xs text-muted-foreground ml-2">{selected.id}</span>
              </CardTitle>
              <div className="flex gap-2">
                {canWrite && (
                  <Button size="sm" onClick={() => setVersionOpen(true)}
                          data-testid="campaign-new-version-btn">New version</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}
                        data-testid="campaign-close">Close</Button>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div><strong>Type:</strong> {selectedCurrentVersion.campaignType}</div>
              <div><strong>Status:</strong> <StatusBadge value={selectedCurrentVersion.status} /></div>
              <div><strong>Method:</strong> {selectedCurrentVersion.fundraisingMethod}</div>
              <div><strong>Min age:</strong> {selectedCurrentVersion.minimumAge}</div>
              <div><strong>Start:</strong> {isoDate(selectedCurrentVersion.startDate)}</div>
              <div><strong>End:</strong> {isoDate(selectedCurrentVersion.endDate) || "—"}</div>
              <div className="col-span-2">
                <strong>Products:</strong> {(selectedCurrentVersion.supportedProducts || []).join(", ") || "—"}
              </div>
              <div><strong>Referral:</strong> {String(!!selectedCurrentVersion.referralEnabled)}</div>
              <div><strong>Statements:</strong> {String(selectedCurrentVersion.statementsEnabled !== false)} (informational only)</div>
            </CardContent>
          </Card>
        )}
      </CardContent>
      <CampaignCreateWizard open={createOpen} onOpenChange={setCreateOpen}
        onCreated={refresh} user={user} />
      <CampaignVersionWizard open={versionOpen} onOpenChange={setVersionOpen}
        campaign={selected} currentVersion={selectedCurrentVersion}
        onVersioned={() => { refresh(); if (selected) openDetails(selected); }} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab: Pricing rules
// ─────────────────────────────────────────────────────────────────────────

function PricingRulesTab({ user, canWrite }) {
  const { rows, loading, refresh } = useResource("/pricing-rules");
  const [selected, setSelected] = useState(null);
  const [selectedCurrentVersion, setSelectedCurrentVersion] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const openDetails = async (r) => {
    setSelected(r); setSelectedCurrentVersion(null);
    try {
      const rr = await axios.get(`${API}/pricing-rules/${r.id}`, auth());
      setSelectedCurrentVersion(rr.data.currentVersion);
    } catch (e) { toast.error(humaniseError(e)); }
  };
  return (
    <Card data-testid="engage-v2-pricing-card">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Pricing Rules</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh}
                  data-testid="pricing-refresh">Refresh</Button>
          {canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    data-testid="pricing-create-btn">New Pricing Rule</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table data-testid="engage-v2-pricing-table">
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Organisation</TableHead>
            <TableHead>Current version</TableHead><TableHead>Updated</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {loading && <LoadingRow colSpan={5} />}
            {!loading && rows.length === 0 &&
              <TableRow><TableCell colSpan={5}><EmptyState label="pricing rules" /></TableCell></TableRow>}
            {!loading && rows.map((r) => (
              <TableRow key={r.id} data-testid={`pricing-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.id}</TableCell>
                <TableCell className="font-mono text-xs">{r.organisationId}</TableCell>
                <TableCell className="font-mono text-xs">{r.currentVersionId}</TableCell>
                <TableCell className="text-xs">{r.updatedAt}</TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => openDetails(r)}
                                     data-testid={`pricing-open-${r.id}`}>Details</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {selected && selectedCurrentVersion && (
          <Card className="mt-4" data-testid="pricing-details">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Pricing rule <span className="font-mono text-sm">{selected.id}</span>
              </CardTitle>
              <div className="flex gap-2">
                {canWrite && (
                  <Button size="sm" onClick={() => setVersionOpen(true)}
                          data-testid="pricing-new-version-btn">New version</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}
                        data-testid="pricing-close">Close</Button>
              </div>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div><strong>Model:</strong> {selectedCurrentVersion.pricingModel}</div>
              {selectedCurrentVersion.pricingModel === "fixed" && (
                <div><strong>Amount:</strong> £{((selectedCurrentVersion.amountPence || 0) / 100).toFixed(2)}</div>
              )}
              {selectedCurrentVersion.pricingModel === "range" && (
                <div><strong>Range:</strong> £{((selectedCurrentVersion.minPence || 0) / 100).toFixed(2)}
                  – £{((selectedCurrentVersion.maxPence || 0) / 100).toFixed(2)}</div>
              )}
              {selectedCurrentVersion.pricingModel === "tiered" && (
                <div><strong>Tiers:</strong> {(selectedCurrentVersion.tiers || []).length}</div>
              )}
              <div><strong>Effective:</strong> {isoDate(selectedCurrentVersion.effectiveFrom)}
                {selectedCurrentVersion.effectiveTo ? ` → ${isoDate(selectedCurrentVersion.effectiveTo)}` : ""}</div>
              {selectedCurrentVersion.product && <div><strong>Product:</strong> {selectedCurrentVersion.product}</div>}
              {selectedCurrentVersion.campaignId && <div><strong>Campaign:</strong> {selectedCurrentVersion.campaignId}</div>}
              {selectedCurrentVersion.fundraiserId && <div><strong>Fundraiser:</strong> {selectedCurrentVersion.fundraiserId}</div>}
            </CardContent>
          </Card>
        )}
      </CardContent>
      <PricingCreateWizard open={createOpen} onOpenChange={setCreateOpen}
        onCreated={refresh} user={user} />
      <PricingVersionWizard open={versionOpen} onOpenChange={setVersionOpen}
        rule={selected} currentVersion={selectedCurrentVersion}
        onVersioned={() => { refresh(); if (selected) openDetails(selected); }} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab: Statement rules
// ─────────────────────────────────────────────────────────────────────────

function StatementRulesTab({ user, canWrite }) {
  const { rows, loading, refresh } = useResource("/statement-rules");
  const [selected, setSelected] = useState(null);
  const [selectedCurrentVersion, setSelectedCurrentVersion] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const openDetails = async (r) => {
    setSelected(r); setSelectedCurrentVersion(null);
    try {
      const rr = await axios.get(`${API}/statement-rules/${r.id}`, auth());
      setSelectedCurrentVersion(rr.data.currentVersion);
    } catch (e) { toast.error(humaniseError(e)); }
  };
  return (
    <Card data-testid="engage-v2-statement-card">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Statement Rules
          <span className="ml-3 text-xs text-muted-foreground">
            (informational only — no payment execution)
          </span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh}
                  data-testid="statement-refresh">Refresh</Button>
          {canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    data-testid="statement-create-btn">New Statement Rule</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table data-testid="engage-v2-statement-table">
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Organisation</TableHead>
            <TableHead>Current version</TableHead><TableHead>Updated</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {loading && <LoadingRow colSpan={5} />}
            {!loading && rows.length === 0 &&
              <TableRow><TableCell colSpan={5}><EmptyState label="statement rules" /></TableCell></TableRow>}
            {!loading && rows.map((r) => (
              <TableRow key={r.id} data-testid={`statement-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.id}</TableCell>
                <TableCell className="font-mono text-xs">{r.organisationId}</TableCell>
                <TableCell className="font-mono text-xs">{r.currentVersionId}</TableCell>
                <TableCell className="text-xs">{r.updatedAt}</TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => openDetails(r)}
                                     data-testid={`statement-open-${r.id}`}>Details</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {selected && selectedCurrentVersion && (
          <Card className="mt-4" data-testid="statement-details">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Statement rule <span className="font-mono text-sm">{selected.id}</span>
              </CardTitle>
              <div className="flex gap-2">
                {canWrite && (
                  <Button size="sm" onClick={() => setVersionOpen(true)}
                          data-testid="statement-new-version-btn">New version</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}
                        data-testid="statement-close">Close</Button>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div><strong>Frequency:</strong> {selectedCurrentVersion.frequency}</div>
              <div><strong>Grouping:</strong> {selectedCurrentVersion.grouping}</div>
              <div><strong>VAT:</strong> {selectedCurrentVersion.vatHandling}</div>
              <div><strong>Approval:</strong> {selectedCurrentVersion.approvalWorkflow}</div>
              <div><strong>Prefix:</strong> {selectedCurrentVersion.referencePrefix}</div>
              <div><strong>Effective:</strong> {isoDate(selectedCurrentVersion.effectiveFrom)}
                {selectedCurrentVersion.effectiveTo ? ` → ${isoDate(selectedCurrentVersion.effectiveTo)}` : ""}</div>
            </CardContent>
          </Card>
        )}
      </CardContent>
      <StatementCreateWizard open={createOpen} onOpenChange={setCreateOpen}
        onCreated={refresh} user={user} />
      <StatementVersionWizard open={versionOpen} onOpenChange={setVersionOpen}
        rule={selected} currentVersion={selectedCurrentVersion}
        onVersioned={() => { refresh(); if (selected) openDetails(selected); }} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab: Assignments
// ─────────────────────────────────────────────────────────────────────────

function AssignmentsTab({ user, canWrite, campaigns }) {
  const { rows, loading, refresh } = useResource("/assignments");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [targetTypeFilter, setTargetTypeFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const filtered = rows.filter((a) =>
    (campaignFilter === "" || a.campaignId === campaignFilter) &&
    (targetTypeFilter === "__all__" || a.targetType === targetTypeFilter) &&
    (statusFilter === "__all__" || a.status === statusFilter));
  const openDetails = async (a) => {
    setSelected(a); setHistory([]);
    try {
      const r = await axios.get(`${API}/assignments/${a.id}/history`, auth());
      setHistory(r.data.history || []);
    } catch (e) { toast.error(humaniseError(e)); }
  };
  return (
    <Card data-testid="engage-v2-assignments-card">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Assignments</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh}
                  data-testid="assignments-refresh">Refresh</Button>
          {canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    data-testid="assignments-create-btn">New Assignment</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Campaign id</Label>
            <Input value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}
                   data-testid="assignments-filter-campaign" />
          </div>
          <div>
            <Label className="text-xs">Target type</Label>
            <Select value={targetTypeFilter} onValueChange={setTargetTypeFilter}>
              <SelectTrigger data-testid="assignments-filter-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {ASSIGNMENT_TARGETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="assignments-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {ASSIGNMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Table data-testid="engage-v2-assignments-table">
          <TableHeader><TableRow>
            <TableHead>ID</TableHead><TableHead>Campaign</TableHead>
            <TableHead>Target</TableHead><TableHead>Status</TableHead>
            <TableHead>Effective</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {loading && <LoadingRow colSpan={6} />}
            {!loading && filtered.length === 0 &&
              <TableRow><TableCell colSpan={6}><EmptyState label="assignments" /></TableCell></TableRow>}
            {!loading && filtered.map((a) => (
              <TableRow key={a.id} data-testid={`assignment-row-${a.id}`}>
                <TableCell className="font-mono text-xs">{a.id}</TableCell>
                <TableCell className="font-mono text-xs">{a.campaignId}</TableCell>
                <TableCell>{a.targetType}<span className="font-mono text-xs text-muted-foreground ml-1">{a.targetId}</span></TableCell>
                <TableCell><StatusBadge value={a.status} /></TableCell>
                <TableCell className="text-xs">{isoDate(a.effectiveFrom)}</TableCell>
                <TableCell className="space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => openDetails(a)}
                          data-testid={`assignment-open-${a.id}`}>Details</Button>
                  {canWrite && a.status !== "active" && (
                    <AssignmentLifecycleButton assignment={a} action="activate" label="Activate" onChanged={refresh} />
                  )}
                  {canWrite && a.status === "active" && (
                    <AssignmentLifecycleButton assignment={a} action="suspend" label="Suspend" onChanged={refresh} />
                  )}
                  {canWrite && (
                    <>
                      <AssignmentLifecycleButton assignment={a} action="expire" label="Expire" onChanged={refresh} />
                      <AssignmentLifecycleButton assignment={a} action="terminate" label="Terminate" onChanged={refresh} />
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {selected && (
          <Card className="mt-4" data-testid="assignment-details">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Assignment <span className="font-mono text-sm">{selected.id}</span>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}
                      data-testid="assignment-close">Close</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><strong>Campaign:</strong> {selected.campaignId}</div>
                <div><strong>Target:</strong> {selected.targetType} / {selected.targetId}</div>
                <div><strong>Status:</strong> <StatusBadge value={selected.status} /></div>
                <div><strong>Effective:</strong> {isoDate(selected.effectiveFrom)}
                  {selected.effectiveTo ? ` → ${isoDate(selected.effectiveTo)}` : ""}</div>
              </div>
              <div>
                <div className="text-sm font-semibold mt-4">Append-only history</div>
                <Table data-testid="assignment-history-table">
                  <TableHeader><TableRow>
                    <TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>By</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {history.length === 0 &&
                      <TableRow><TableCell colSpan={3}><EmptyState label="history" testid="assignment-history-empty" /></TableCell></TableRow>}
                    {history.map((h) => (
                      <TableRow key={h.id} data-testid={`assignment-history-row-${h.id}`}>
                        <TableCell className="text-xs">{h.at}</TableCell>
                        <TableCell><Badge variant="outline">{h.action}</Badge></TableCell>
                        <TableCell className="font-mono text-xs">{h.actorId}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
      <AssignmentCreateWizard open={createOpen} onOpenChange={setCreateOpen}
        onCreated={refresh} user={user} campaigns={campaigns} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────────────────

export default function CommercialManagementConsole() {
  const { user: ctxUser } = useAuth();
  const user = ctxUser || readCurrentUser();
  const canWrite = WRITE_ROLES.has((user?.role || "").toLowerCase());

  // Reference lists used by the contract wizard + assignment wizard
  const [campaigns, setCampaigns] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [statementRules, setStatementRules] = useState([]);

  const reloadRefs = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        axios.get(`${API}/campaigns`, auth()),
        axios.get(`${API}/pricing-rules`, auth()),
        axios.get(`${API}/statement-rules`, auth()),
      ]);
      setCampaigns(c.data.campaigns || []);
      setPricingRules(p.data.pricingRules || []);
      setStatementRules(s.data.statementRules || []);
    } catch { /* silent — tabs will surface their own errors */ }
  }, []);

  useEffect(() => { document.title = "Engage — Commercial Management"; }, []);
  useEffect(() => { reloadRefs(); }, [reloadRefs]);

  if (!user) return <PermissionDenied />;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="engage-v2-console">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Commercial Management</h1>
          <p className="text-sm text-muted-foreground">
            Governed contracts, campaigns, pricing, statement rules and assignments.
            Statements-only — no payment execution.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="engage-v2-mode-badge">Engage v2</Badge>
          {canWrite ? (
            <Badge data-testid="engage-v2-role-badge">write</Badge>
          ) : (
            <Badge variant="secondary" data-testid="engage-v2-role-badge">read-only</Badge>
          )}
        </div>
      </div>
      <Tabs defaultValue="contracts" className="w-full">
        <TabsList className="grid grid-cols-7 w-full" data-testid="engage-v2-tabs">
          <TabsTrigger value="contracts" data-testid="engage-v2-tab-contracts">Contracts</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="engage-v2-tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="engage-v2-tab-pricing">Pricing</TabsTrigger>
          <TabsTrigger value="statement-rules" data-testid="engage-v2-tab-statement-rules">Statement rules</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="engage-v2-tab-assignments">Assignments</TabsTrigger>
          <TabsTrigger value="version-history" data-testid="engage-v2-tab-version-history">Version History</TabsTrigger>
          <TabsTrigger value="routes" data-testid="engage-v2-tab-routes">Route inventory</TabsTrigger>
        </TabsList>
        <TabsContent value="contracts">
          <ContractsTab user={user} canWrite={canWrite}
            campaigns={campaigns} pricingRules={pricingRules} statementRules={statementRules} />
        </TabsContent>
        <TabsContent value="campaigns">
          <CampaignsTab user={user} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="pricing">
          <PricingRulesTab user={user} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="statement-rules">
          <StatementRulesTab user={user} canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="assignments">
          <AssignmentsTab user={user} canWrite={canWrite} campaigns={campaigns} />
        </TabsContent>
        <TabsContent value="version-history">
          <VersionHistoryPanel />
        </TabsContent>
        <TabsContent value="routes"><RouteInventoryPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
